import { FormEvent, useEffect, useRef, useState } from 'react';
import { animate, createScope, stagger } from 'animejs';

import { LoginBrand } from '../../../components/Logo/LoginBrand';
import { prefersReducedMotion } from '../../../lib/motion/prefersReducedMotion';
import { useAuth } from '../../../providers/AuthUserProvider';
import { extractApiErrorMessage } from '../../../utils/extractApiErrorMessage';
import { useLoginMutation } from '../api/useLoginMutation';

export const LoginPage = () => {
  const { login } = useAuth();
  const loginMutation = useLoginMutation();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) {
      return;
    }

    const scope = createScope({ root }).add(() => {
      animate(root.querySelectorAll('[data-motion-item]'), {
        opacity: { from: 0, to: 1 },
        translateY: { from: 18, to: 0 },
        delay: stagger(90, { start: 120 }),
        duration: 560,
        ease: 'out(3)',
      });
    });

    return () => {
      scope.revert();
    };
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage('');

    try {
      const data = await loginMutation.mutateAsync({
        email,
        password,
        rememberMe,
      });
      login(data, rememberMe);
    } catch (error) {
      setErrorMessage(
        extractApiErrorMessage(error, 'Email ou senha inválidos'),
      );
    }
  };

  return (
    <div className="login-page motion-reveal" ref={rootRef}>
      <section className="login-panel">
        <div data-motion-item>
          <LoginBrand />
        </div>

        <div className="login-content">
          <h1 className="login-title" data-motion-item>
            Entre na sua conta
          </h1>
          <p className="login-subtitle" data-motion-item>
            Seja bem-vindo(a) de volta!
          </p>

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-field" data-motion-item>
              <label htmlFor="email">Email</label>
              <div className="login-input-wrap">
                <span className="login-input-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </span>
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div className="login-field" data-motion-item>
              <label htmlFor="password">Senha</label>
              <div className="login-input-wrap">
                <span className="login-input-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6a5 5 0 0 0-10 0v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-7 8.73V17h2v-.27a2 2 0 1 0-2 0zM9 8V6a3 3 0 0 1 6 0v2H9z" />
                  </svg>
                </span>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    {showPassword ? (
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                    ) : (
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div className="login-options" data-motion-item>
              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                />
                Lembrar informações
              </label>
              <a className="login-forgot" href="#">
                Esqueci minha senha
              </a>
            </div>

            {errorMessage ? (
              <p className="login-error">{errorMessage}</p>
            ) : null}

            <button
              type="submit"
              className="login-submit"
              data-motion-item
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        </div>

        <footer className="login-footer" data-motion-item>
          <span className="login-footer-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.79 1.42-1.41zM4 10.5H1v2h3v-2zm9-9.95h-2V3.5h2V.55zM20.45 4.46l-1.41-1.41-1.79 1.79 1.41 1.42 1.79-1.8zM17.24 18.16l1.79 1.8 1.41-1.41-1.8-1.79-1.4 1.4zM20 10.5v2h3v-2h-3zm-8-5c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm-1 16.95h2V19.5h-2v2.95zm-7.45-1.46 1.41 1.41 1.79-1.8-1.41-1.41-1.79 1.8z" />
            </svg>
          </span>
          <div className="login-footer-phrases">
            <span>Energia inteligente.</span>
            <span className="login-footer-divider" aria-hidden="true">
              |
            </span>
            <span>Economia de verdade.</span>
            <span className="login-footer-divider" aria-hidden="true">
              |
            </span>
            <span>Um futuro sustentável.</span>
          </div>
        </footer>
      </section>
    </div>
  );
};
